import React from 'react';
import { useVisibilityChange } from '../../CustomHooks/UseVisibilityChange';

import { readCart, saveCart } from '../Api/ShopwareCart';
import { ActionType, CartContextInterface, CartItemType, ShopwareCart, ShopwareCartWithMessages } from '../Cart/Types';
import { AssignedConsultant } from '../MyLom/Account/ConsultantAssignment/Types';
import { AccountContext } from './AccountContext';
import { AssignedConsultantContext } from './AssignedConsultantsContext';
import { FilterContext } from './FilterContext';
import { LocalCartContext } from './LocalCartContext';

const initialCart = (): ShopwareCartWithMessages => {
    return {
        mode: 'shopware',
        carts: null,
        visible: false,
        cartMessages: [],
    };
};

// we need this to determine if we
// are currently waiting in a timeout
// for the ``latestAction`` state to be
// cleared (for notification to vanish)
let cleartingLatestAction = false;

export const ShopwareCartContext = React.createContext<CartContextInterface>({
    cart: initialCart(),
    updateCartItems: (item, action) => {},
    setCartVisibility: () => {},
    activeItems: [],
    latestAction: null,
    syncFailure: false,
    totalItemsInCarts: 0,
    deleteCart: () => {},
    refreshCart: (visibilityOverride?: boolean) => {},
});

export const ShopwareCartContextValueProvider: React.FunctionComponent = (props) => {
    const { accountData } = React.useContext(AccountContext);
    const { assignedConsultants } = React.useContext(AssignedConsultantContext);
    const { accountOrProfile } = React.useContext(FilterContext);
    const defaultConsultant = assignedConsultants.find((consultant) => consultant.default === true);
    const [cart, updateCart] = React.useState<ShopwareCartWithMessages>(initialCart());
    const [latestAction, setLatestAction] = React.useState<ActionType | null>(null);
    const [syncFailure, setSyncFailure] = React.useState<boolean>(false);

    if (!accountData) {
        throw new Error('can not render component without account data');
    }

    if (!accountOrProfile) {
        throw new Error('can not render component without filter data');
    }

    // synch shopware cart with local cart on initial load
    const localCart = React.useContext(LocalCartContext).cart;
    const deleteLocalCart = React.useContext(LocalCartContext).deleteCart;

    React.useEffect(() => {
        if (localCart.mode === 'local' && localCart.items.length > 0) {
            const newCart = { ...cart };
            newCart.carts = {};
            newCart.carts[accountOrProfile.id] = {};
            newCart.carts[accountOrProfile.id].items = localCart.items;

            sendCartToServer(newCart, defaultConsultant, () => {
                updateCart(newCart);
                deleteLocalCart();
            });
        }
    }, [localCart]);

    // Initial read from server
    const fetchCartFromServer = (cartVisibility?: boolean) =>
        readCart(
            accountData,
            (carts, cartMessages) => {
                const newCart = { ...cart, cartMessages };
                newCart.carts = carts;
                if (cartVisibility) {
                    newCart.visible = true;
                }
                updateCart(newCart);
                setSyncFailure(false);
            },
            (error) => {
                setSyncFailure(true);
                console.error('Fatal error while reading initial cart', error);
            },
            (error) => {
                setSyncFailure(true);
                console.error('Fatal error while reading initial cart', error);
            },
        );

    const refreshCart = (visibilityOverride?: boolean) => {
        fetchCartFromServer(visibilityOverride);
    };

    React.useEffect(() => {
        fetchCartFromServer();
    }, []);

    useVisibilityChange(fetchCartFromServer);

    /**
     * Read server cart first an merge to reduce risk of inconsistency
     * We apply the changes on the current cart right away, to be more responsive
     * The second update is applied on the server-site cart asynchronously
     *
     * @param updateItem
     * @param action
     * @param customerId Removals can also occur for items outside the active customer
     */
    const updateCartItems = (updateItem: CartItemType, action: ActionType): void => {
        if (!accountOrProfile) {
            throw new Error('missing account for active filter in cart');
        }

        // this is the optimistic application for a fast response in the user interface
        // in case of a failure the item is removed again
        updateCart({ ...applyCartAction(cart) });

        // this is the the second run where we first read the cart from the server
        // to prevent inconsistencies within multiple tabs and send
        // the freshly modified cart to the server afterwards

        // TODO: Throttle the amount of calls to this function to prevent
        // race conditions when updating the cart (for example when deleting many items fast)
        // OR hide certain interactions while server is synchronizing
        readCart(
            accountData,
            (carts) => {
                const serverCart = { ...cart };
                serverCart.carts = carts;
                const newCart = applyCartAction(serverCart);
                if (newCart) {
                    sendCartToServer(
                        newCart,
                        defaultConsultant,
                        () => {
                            // read cart from server again
                            // since there could be certain properties updated by
                            // by Shopware (for example ``meetingDiscount``)
                            fetchCartFromServer(true);
                            setSyncFailure(false);
                        },
                        () => {
                            removeItemOnSyncFailure(updateItem);
                        },
                    );
                }
            },
            (error) => {
                setSyncFailure(true);
                console.error('Fatal error while reading initial cart', error);
                removeItemOnSyncFailure(updateItem);
            },
            (error) => {
                setSyncFailure(true);
                console.error('Fatal error while reading initial cart', error);
                removeItemOnSyncFailure(updateItem);
            },
        );

        /**
         * In case the synchronisation with Shopware resulted in a failure,
         * we delete the the item from the visible cart (which has been added
         * optimisticly before any request happened)
         */
        const removeItemOnSyncFailure = (itemToRemove: CartItemType) => {
            updateCart((currentCart) => {
                const filteredCart = { ...currentCart };
                if (filteredCart.carts && filteredCart.carts[accountOrProfile.id]) {
                    filteredCart.carts[accountOrProfile.id].items = filteredCart.carts[
                        accountOrProfile.id
                    ].items.filter((item) => item.articleNumber !== itemToRemove.articleNumber);
                }

                return filteredCart;
            });
        };

        function applyCartAction(
            theCart: ShopwareCartWithMessages,
            customerId = accountOrProfile ? accountOrProfile.id : null,
        ): ShopwareCartWithMessages {
            if (!customerId) {
                throw new Error('can not apply cart action with missing customer id');
            }

            // reset latest action after 5 second timeout
            const resetLatestAction = () => {
                if (cleartingLatestAction === false) {
                    setTimeout(() => {
                        setLatestAction(null);
                        cleartingLatestAction = false;
                    }, 5000);
                }
                cleartingLatestAction = true;
            };
            const newCart = { ...theCart };

            if (!newCart.carts) {
                throw new Error('missing carts that should have been auto-created');
            }

            switch (action) {
                case ActionType.Add:
                    // remove possible duplicates because on an runtime error
                    newCart.carts[customerId].items = newCart.carts[customerId].items.filter(
                        (cartItem) => cartItem.articleNumber !== updateItem.articleNumber,
                    );

                    if (updateItem.quantity !== 0) {
                        newCart.carts[customerId].items.push(updateItem);
                        newCart.visible = true;
                        setLatestAction(ActionType.Add);
                    }

                    break;

                case ActionType.Remove:
                    newCart.carts[customerId].items = newCart.carts[customerId].items.filter(
                        (cartItem) => cartItem.articleNumber !== updateItem.articleNumber,
                    );
                    break;

                case ActionType.Update:
                    // if quantity is 0, remove element
                    newCart.carts[customerId].items = newCart.carts[customerId].items.filter((cartItem) => {
                        if (cartItem.articleNumber === updateItem.articleNumber) {
                            return updateItem.quantity !== 0;
                        }

                        return true;
                    });
                    // if element was found in cart, update
                    newCart.carts[customerId].items = newCart.carts[customerId].items.map((cartItem) => {
                        if (cartItem.articleNumber === updateItem.articleNumber) {
                            return updateItem;
                        } else {
                            return cartItem;
                        }
                    });
                    setLatestAction(ActionType.Update);
                    newCart.visible = true;
                    break;
            }
            resetLatestAction();

            return newCart;
        }
    };

    const setCartVisibility = (visibility: boolean) => {
        const newCart = { ...cart };
        newCart.visible = visibility;
        updateCart(newCart);
    };

    const activeItems = cart.carts ? cart.carts[accountOrProfile.id].items : [];

    function sendCartToServer(
        cartToBeSent: ShopwareCart,
        defaultConsultantToBeSent: AssignedConsultant | undefined,
        onSuccess?: () => void,
        onFailure?: () => void,
    ): void {
        if (!accountData) {
            throw new Error('can not render component without account data');
        }

        // update server cart
        saveCart(
            accountData.id,
            cartToBeSent,
            defaultConsultantToBeSent ? defaultConsultantToBeSent.id : null,
            (json) => {
                if (typeof onSuccess === 'function') {
                    onSuccess();
                }
            },
            (error) => {
                setSyncFailure(true);
                console.warn('Cart sync failed', error);
                if (typeof onFailure === 'function') {
                    onFailure();
                }
            },
            (error) => {
                setSyncFailure(true);
                console.warn('Cart sync failed', error);
                if (typeof onFailure === 'function') {
                    onFailure();
                }
            },
        );
    }

    const totalItemsInCarts = (): number => {
        let count = 0;
        if (!cart.carts) {
            return count;
        }
        Object.values(cart.carts).map((c) => (count += c.items.length));

        return count;
    };

    const deleteCart = () => {
        updateCart((prevCart) => ({ ...initialCart(), visible: prevCart.visible }));
        sendCartToServer(initialCart(), undefined);
    };

    return (
        <ShopwareCartContext.Provider
            value={{
                cart,
                updateCartItems,
                setCartVisibility,
                activeItems,
                latestAction,
                syncFailure,
                totalItemsInCarts: totalItemsInCarts(),
                deleteCart,
                refreshCart,
            }}
        >
            {props.children}
        </ShopwareCartContext.Provider>
    );
};
